import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../../lib/supabase';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../hooks/useToast';
import { formatCurrency } from '../../../utils/format';
import { Plus } from 'lucide-react';
import WithdrawalModal from './WithdrawalModal';
import { ClientLayout } from '../../../components/layout/ClientLayout';

interface Withdrawal {
  id: string;
  amount: number;
  status: 'pending' | 'approved' | 'rejected';
  payment_method: 'mobile_money' | 'bank_transfer';
  phone_number?: string;
  bank_details?: any;
  created_at: string;
  updated_at: string;
  notes?: string;
}

const WithdrawalList = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { toast } = useToast();

  const { data: withdrawals, isLoading, refetch } = useQuery({
    queryKey: ['withdrawals'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('withdrawals')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Withdrawal[];
    }
  });

  const handleWithdrawalSuccess = () => {
    setIsModalOpen(false);
    refetch();
    toast({
      title: 'Demande de retrait envoyée',
      description: 'Votre demande de retrait a été enregistrée avec succès.',
      variant: 'success'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'text-green-400';
      case 'rejected':
        return 'text-red-400';
      default:
        return 'text-yellow-400';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'approved':
        return 'Approuvé';
      case 'rejected':
        return 'Rejeté';
      default:
        return 'En attente';
    }
  };

  return (
    <ClientLayout>
      <div className="container mx-auto py-8 pt-20 space-y-8">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Retraits</h1>
          <Button onClick={() => setIsModalOpen(true)} className="bg-primary hover:bg-primary/90">
            <Plus className="h-4 w-4 mr-2" />
            Nouveau retrait
          </Button>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden backdrop-blur-lg">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-400">Date</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-400">Montant</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-400">Méthode</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-400">Statut</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-400">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {withdrawals?.map((withdrawal) => (
                  <tr key={withdrawal.id} className="hover:bg-white/5">
                    <td className="px-6 py-4 text-sm text-gray-300">
                      {new Date(withdrawal.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-white">
                      {formatCurrency(withdrawal.amount)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-300">
                      {withdrawal.payment_method === 'mobile_money' ? 'Mobile Money' : 'Virement bancaire'}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className={getStatusColor(withdrawal.status)}>
                        {getStatusText(withdrawal.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-300">
                      {withdrawal.notes || '-'}
                    </td>
                  </tr>
                ))}
                {!isLoading && (!withdrawals || withdrawals.length === 0) && (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center text-gray-400">
                      Aucun retrait trouvé
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <WithdrawalModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSuccess={handleWithdrawalSuccess}
        />
      </div>
    </ClientLayout>
  );
};

export default WithdrawalList;
